import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { createStructuredSelector } from "reselect";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { BlueBtn } from "components/Controls";
import { Spinner } from "components/DataDisplay";
import { tokenSelector } from "containers/EmbeddableWidget/selectors";
import { selectedOfferSelector } from "containers/MultistepForm/selectors";
import { finishApplication } from "containers/MultistepForm/actions";
import { ProvidersApi } from "api/providers";
import wrapAgreementHtml from "./wrapAgreementHtml";
import "./styles.scss";

class FypnContract extends Component {
  state = {
    agreements: [],
    loadingAgreements: true,
    loadError: null,
    stepIndex: 0,
    fullName: "",
    stepError: null,
    signing: false,
    allComplete: false,
  };

  signatures = {};
  iframeRef = React.createRef();
  scrollNodeRef = React.createRef();
  resizeObserver = null;
  srcDocCache = {};
  OPTIONAL_CHECKBOX_IDS = ["smsMessages"];

  componentDidMount() {
    this.fetchAgreements();
  }

  componentWillUnmount() {
    this.disconnectResize();
  }

  fetchAgreements = () => {
    const { token, selectedOffer } = this.props;
    this.setState({ loadingAgreements: true, loadError: null });

    return ProvidersApi.fypnAgreementPdf(token, { app_id: selectedOffer.app_id })
      .then((resp) => {
        this.setState({
          agreements: resp.agreements || [],
          loadingAgreements: false,
          stepIndex: 0,
          stepError: null,
          allComplete: false,
        });
      })
      .catch(() => {
        this.setState({
          loadingAgreements: false,
          loadError: "Could not load loan agreement. Please try again.",
        });
      });
  };

  currentAgreement() {
    return this.state.agreements[this.state.stepIndex];
  }

  isLastStep() {
    return this.state.stepIndex === this.state.agreements.length - 1;
  }

  getSrcDoc(agreement) {
    if (!this.srcDocCache[agreement.item_key]) {
      this.srcDocCache[agreement.item_key] = wrapAgreementHtml(agreement.html);
    }
    return this.srcDocCache[agreement.item_key];
  }

  requiredName() {
    const agreement = this.currentAgreement();
    const match = /verify(?:Signature|NamedSignatureItem)\(\s*signatureItems\s*,\s*'([^']*)'/.exec(
      (agreement && agreement.html) || ""
    );
    if (!match) return null;
    const ta = document.createElement("textarea");
    ta.innerHTML = match[1];
    return ta.value;
  }

  computedInitials() {
    return this.state.fullName
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase();
  }

  onIframeLoad = () => {
    const iframe = this.iframeRef.current;
    const doc = iframe && iframe.contentDocument;
    const win = iframe && iframe.contentWindow;
    if (!doc) return;

    this.syncSignatureIntoDocument();
    doc.addEventListener("change", this.onDocChange, true);

    this.fitIframeHeight();
    setTimeout(this.fitIframeHeight, 80);
    setTimeout(this.fitIframeHeight, 350);
    this.disconnectResize();
    if (win && win.ResizeObserver && doc.body) {
      this.resizeObserver = new win.ResizeObserver(this.fitIframeHeight);
      this.resizeObserver.observe(doc.body);
    }

    this.recomputeComplete();
  };

  disconnectResize() {
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
      } catch (e) {
        // ignore
      }
      this.resizeObserver = null;
    }
  }

  onDocChange = (e) => {
    const t = e.target;
    if (!t || (t.type !== "checkbox" && t.type !== "radio")) return;
    if (t.checked && t.name) {
      t.ownerDocument.querySelectorAll(`input[name="${t.name}"]`).forEach((i) => {
        if (i !== t) i.checked = false;
      });
    }
    if (t.checked) t.classList.remove("fypn-focus");
    this.recomputeComplete();
  };

  fitIframeHeight = () => {
    const iframe = this.iframeRef.current;
    const doc = iframe && iframe.contentDocument;
    if (!doc || !doc.body) return;
    const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
    iframe.style.height = `${h}px`;
  };

  syncSignatureIntoDocument = () => {
    const doc = this.iframeRef.current && this.iframeRef.current.contentDocument;
    if (!doc) return;
    const name = this.state.fullName.trim();
    const initials = this.computedInitials();
    doc.querySelectorAll('input.signature, input[class^="signature_"]').forEach((i) => {
      i.value = name;
      i.readOnly = true;
    });
    doc.querySelectorAll("input.initials").forEach((i) => {
      i.value = initials;
      i.readOnly = true;
    });
  };

  onNameChange = (e) => {
    this.setState({ fullName: e.target.value, stepError: null }, () => {
      this.syncSignatureIntoDocument();
      this.recomputeComplete();
    });
  };

  fields() {
    const doc = this.iframeRef.current && this.iframeRef.current.contentDocument;
    if (!doc) return [];
    return Array.from(
      doc.querySelectorAll(
        'input.signature, input[class^="signature_"], input.initials, input[type="checkbox"], input[type="radio"]'
      )
    );
  }

  orderedFields() {
    return this.fields().sort(
      (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
    );
  }

  isIncomplete(el) {
    if (el.type === "checkbox" || el.type === "radio") {
      if (this.OPTIONAL_CHECKBOX_IDS.indexOf(el.id) !== -1) return false;
      if (el.checked) return false;
      if (el.name) {
        const group = el.ownerDocument.querySelectorAll(`input[name="${el.name}"]`);
        return !Array.from(group).some((i) => i.checked);
      }
      return true;
    }
    return !el.value || !el.value.trim();
  }

  recomputeComplete = () => {
    if (!this.iframeRef.current || !this.iframeRef.current.contentDocument) return;
    const complete = this.fields().every((el) => !this.isIncomplete(el));
    if (complete !== this.state.allComplete) this.setState({ allComplete: complete });
  };

  contentYOf(el) {
    const iframe = this.iframeRef.current;
    const scrollNode = this.scrollNodeRef.current;
    return (
      iframe.getBoundingClientRect().top +
      el.getBoundingClientRect().top -
      scrollNode.getBoundingClientRect().top +
      scrollNode.scrollTop
    );
  }

  focusField(el) {
    const doc = el.ownerDocument;
    doc.querySelectorAll(".fypn-focus").forEach((n) => n.classList.remove("fypn-focus"));
    el.classList.add("fypn-focus");
  }

  jumpToNextField = () => {
    const iframe = this.iframeRef.current;
    const scrollNode = this.scrollNodeRef.current;
    if (!iframe || !iframe.contentDocument || !scrollNode) return null;

    const incomplete = this.orderedFields().filter((el) => this.isIncomplete(el));
    if (incomplete.length === 0) {
      scrollNode.scrollTo({ top: scrollNode.scrollHeight, behavior: "smooth" });
      return null;
    }

    const threshold = scrollNode.scrollTop + 12;
    const target = incomplete.find((el) => this.contentYOf(el) > threshold) || incomplete[0];
    scrollNode.scrollTo({ top: Math.max(0, this.contentYOf(target) - 40), behavior: "smooth" });
    this.focusField(target);
    return target;
  };

  buildSignatureJson() {
    const doc = this.iframeRef.current && this.iframeRef.current.contentDocument;
    if (!doc) {
      this.setState({ stepError: "Could not load the document. Please try again." });
      return null;
    }

    this.syncSignatureIntoDocument();

    const name = this.state.fullName.trim();
    const requiredName = this.requiredName();
    if (requiredName && name && name.toUpperCase() !== requiredName.toUpperCase()) {
      this.setState({
        stepError: `Your name must be entered exactly as it appears on the document: ${requiredName}`,
      });
      return null;
    }

    const target = this.jumpToNextField();
    if (target) {
      this.setState({
        stepError:
          target.type === "checkbox" || target.type === "radio"
            ? "Please review and check the highlighted box, then sign."
            : "Type your full legal name above so it fills the highlighted signature.",
      });
      return null;
    }

    const items = {};
    const sigs = Array.from(doc.querySelectorAll("input.signature"));
    sigs.forEach((i) => {
      if (i.id) items[i.id] = i.value.trim();
    });
    if (sigs.length) items.signature = sigs[0].value.trim();

    doc.querySelectorAll("input.initials").forEach((i) => {
      if (i.id) items[i.id] = i.value.trim();
    });

    doc.querySelectorAll('input[class^="signature_"]').forEach((i) => {
      const cls = Array.from(i.classList).find((c) => c.indexOf("signature_") === 0);
      const named = cls && cls.slice("signature_".length);
      if (named) {
        items[`${named}.signature`] = i.value.trim();
        items[`${named}.signatureipaddress`] = "[IPADDRESS]";
        items[`${named}.signaturetime`] = "[CURRENTDATE]";
      }
    });

    doc.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((c) => {
      if (c.id) items[c.id] = c.checked ? "1" : "0";
    });

    const sms = doc.querySelector("#smsMessages");
    if (sms) {
      items.contactmethods_textmessages = sms.checked ? "LCM" : "";
      items.contactmethods_phone = sms.checked ? "LCM" : "";
    }
    const moneyOrder = doc.querySelector("#moneyOrderOrCertifiedCheckChk");
    if (moneyOrder && moneyOrder.checked) {
      items.allowedpaymenttype_ach = "false";
      items.allowedpaymenttype_debitcard = "false";
      items.preferredautoextensiontype = "none";
    }

    return {
      item_key: this.currentAgreement().item_key,
      signature_data_json: JSON.stringify(items),
    };
  }

  goNext = () => {
    const sig = this.buildSignatureJson();
    if (!sig) return;
    this.signatures[sig.item_key] = sig.signature_data_json;

    if (this.isLastStep()) {
      this.submitSignatures();
    } else {
      this.setState((prev) => ({
        stepIndex: prev.stepIndex + 1,
        stepError: null,
        allComplete: false,
      }));
    }
  };

  submitSignatures = () => {
    const { token, selectedOffer, finishApplication } = this.props;
    const signatures = this.state.agreements.map((a) => ({
      item_key: a.item_key,
      signature_data_json: this.signatures[a.item_key],
    }));

    this.setState({ signing: true, stepError: null });
    return ProvidersApi.fypnSignAgreement(token, {
      app_id: selectedOffer.app_id,
      signatures,
    })
      .then(() => finishApplication())
      .catch(() =>
        this.setState({
          signing: false,
          stepError: "Could not record your signature. Please try again.",
        })
      );
  };

  render() {
    const {
      agreements,
      loadingAgreements,
      loadError,
      stepIndex,
      fullName,
      stepError,
      signing,
      allComplete,
    } = this.state;

    if (loadingAgreements) {
      return (
        <div className="fypn_contract_step fypn_contract_step--loading">
          <Spinner />
        </div>
      );
    }

    if (loadError || agreements.length === 0) {
      return (
        <div className="fypn_contract_step">
          <p className="fypn-step-error">{loadError || "No loan documents to sign."}</p>
          <BlueBtn onClick={this.fetchAgreements}>Retry</BlueBtn>
        </div>
      );
    }

    const agreement = agreements[stepIndex];
    const requiredName = this.requiredName();
    const last = this.isLastStep();

    return (
      <div className="fypn_contract_step">
        <h2 className="subtitle font-center">Review &amp; sign your loan documents</h2>
        <div className="fypn-doc-progress">
          Document {stepIndex + 1} of {agreements.length}
          {agreement.title ? `: ${agreement.title}` : ""}
        </div>

        <div className="fypn-doc-frame">
          <SimpleBar
            style={{ maxHeight: 380, width: "100%" }}
            scrollableNodeProps={{ ref: this.scrollNodeRef }}
          >
            <div className="fypn-doc-wrapper">
              <iframe
                key={agreement.item_key}
                ref={this.iframeRef}
                id="fypn-contract-iframe"
                title={agreement.title || `agreement-${agreement.item_key}`}
                srcDoc={this.getSrcDoc(agreement)}
                width="100%"
                scrolling="no"
                frameBorder="0"
                onLoad={this.onIframeLoad}
              />
            </div>
          </SimpleBar>
          <button
            type="button"
            className={`fypn-next-tab${allComplete ? " fypn-next-tab--done" : ""}`}
            onClick={this.jumpToNextField}
          >
            {allComplete ? "✓ Done" : "Next field ↓"}
          </button>
        </div>

        <div className="fypn-signature-panel">
          {stepError && <div className="fypn-step-error">{stepError}</div>}
          <label htmlFor="fypn-signature-input">
            Type your full legal name to sign
            {requiredName ? ` (exactly: ${requiredName})` : ""}
          </label>
          <input
            id="fypn-signature-input"
            type="text"
            value={fullName}
            onChange={this.onNameChange}
            placeholder={requiredName || "First Last"}
          />
          <div className="fypn-signature-hint">
            {allComplete
              ? "Everything’s filled in — click below to sign."
              : "Use “Next field” to jump to each box you still need to check, then sign."}
          </div>
          <BlueBtn onClick={this.goNext} loading={signing} disabled={signing || !fullName.trim()}>
            {last ? "I AGREE & SIGN" : "CONTINUE"}
          </BlueBtn>
        </div>
      </div>
    );
  }
}

FypnContract.propTypes = {
  token: PropTypes.string,
  selectedOffer: PropTypes.object,
};

FypnContract.defaultProps = {
  selectedOffer: {},
};

const mapStateToProps = createStructuredSelector({
  token: tokenSelector(),
  selectedOffer: selectedOfferSelector(),
});

const mapDispatchToProps = (dispatch) => ({
  finishApplication: (value) => dispatch(finishApplication(value)),
});

FypnContract = connect(mapStateToProps, mapDispatchToProps)(FypnContract);

export default FypnContract;
